import { ClientSideDataGridModel } from "src/components/common/dataGrid/gridModel/ClientSideDataGridModel";
import {
	CatalogPartRevisionAttributeRulesApi,
	CatalogPartRevisionAttributeRulesApi_AttributeRuleDto
} from "src/api/generated/erp/configurator/management/catalogPart/api/catalogPartRevisionAttributeRulesApi.ts";
import { FormCrudDataGrid } from "src/components/common/dataGrid/crud/FormCrudDataGrid";
import { textColumn } from "src/components/common/dataGrid/columns";
import i18n from "i18next";
import {
	CatalogPartRevisionAttributeRuleForm
} from "src/components/views/erp/configurator/managing/catalogPart/attributeRules/CatalogPartRevisionAttributeRuleForm.tsx";
import { CatalogPartRevisionView } from "src/api/generated/erp/db/types/tables/catalogPartRevisionView.ts";
import { AsyncButton } from "src/components/common/buttons/AsyncButton.tsx";
import { faFileDownload } from "@fortawesome/pro-regular-svg-icons";
import {
	CatalogPartQueryApi
} from "src/api/generated/erp/configurator/management/catalogPart/api/catalogPartQueryApi.ts";
import { CloseDialogParams, useGenericDialog } from "src/components/common/dialogs/GenericDialogContext.ts";
import {
	CatalogPartRevisionSelectionDataGrid
} from "src/components/views/erp/parts/catalogPart/catalogPartRevisionSelection/CatalogPartRevisionSelectionDataGrid.tsx";
import { CatalogPart } from "src/api/generated/erp/db/types/tables/catalogPart.ts";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface CatalogPartRevisionAttributeRulesDataGridProps {
	catalogPartRevision: CatalogPartRevisionView;
	onSelectionChanged: (selectedRow: CatalogPartRevisionAttributeRulesApi_AttributeRuleDto | undefined) => void;
}

export const CatalogPartRevisionAttributeRulesDataGrid = ({
	catalogPartRevision,
	onSelectionChanged,
}: CatalogPartRevisionAttributeRulesDataGridProps) => {
	const editable = catalogPartRevision.state === "INITIAL" || catalogPartRevision.unrestrictedEditingCascaded;

	return (
		<ClientSideDataGridModel
			fetchData={() =>
				CatalogPartRevisionAttributeRulesApi.getCatalogPartRevisionAttributeRules({
					catalogPartRevisionId: catalogPartRevision.catalogPartRevisionId,
				})
			}
			initialParams={{}}
			getRows={(data) => data}
			getRowId={(row) => row.catalogPartRevisionAttributeConfigurationRuleId}
			onSelectionChanged={(rows) => onSelectionChanged(rows[0])}
			gridId={"FC2F91A4F2C090C1"}
			render={({ dataGridProps, refreshData }) => (
				<FormCrudDataGrid<CatalogPartRevisionAttributeRulesApi_AttributeRuleDto>
					disableMultipleRowSelection
					columns={[
						textColumn({
							field: "attributeFieldLabel",
							headerName: i18n.t("attribute"),
							width: 250,
						}),
					]}
					form={{
						addRowEnabled: editable,
						editEnabled: editable,
						dialogSize: "xl",
						dialogTitle: i18n.t("attribute_rule"),
						agentContext: (row) => ({
							key: defineAgentUiContextKey("erp.configurator.catalogPartRevisionAttributeRule.formDialog"),
							title: i18n.t("attribute_rule"),
							data: {
								catalogPartRevisionId: catalogPartRevision.catalogPartRevisionId,
								ruleId: row?.catalogPartRevisionAttributeConfigurationRuleId ?? null,
							},
						}),
						component: ({ row, onCompleted, onFormEdited }) => (
							<CatalogPartRevisionAttributeRuleForm
								catalogPartRevision={catalogPartRevision}
								ruleId={row?.catalogPartRevisionAttributeConfigurationRuleId}
								onCompleted={onCompleted}
								onFormEdited={onFormEdited}
							/>
						),
					}}
					remove={
						editable &&
						(({ row }) =>
							CatalogPartRevisionAttributeRulesApi.delete_({
								ruleId: row.catalogPartRevisionAttributeConfigurationRuleId,
							}))
					}
					actionBarComponents={
						<>
							<CopyRulesFromAnotherRevisionButton
								catalogPartRevision={catalogPartRevision}
								refreshData={refreshData}
							/>
						</>
					}
					agentContext={{
						key: defineAgentUiContextKey("erp.configurator.catalogPartRevisionAttributeRules.grid"),
						data: { catalogPartRevisionId: catalogPartRevision.catalogPartRevisionId },
						rowFields: ["catalogPartRevisionAttributeConfigurationRuleId", "attributeFieldId", "attributeFieldLabel"],
						selectedRowPathSegment: (row) => row.attributeFieldLabel,
					}}
					{...dataGridProps}
				/>
			)}
		/>
	);
};

type CopyRulesFromAnotherRevisionButtonProps = {
	catalogPartRevision: CatalogPartRevisionView;
	refreshData: (params?: Partial<object>) => Promise<CatalogPartRevisionAttributeRulesApi_AttributeRuleDto[]>;
};

const CopyRulesFromAnotherRevisionButton = ({
	catalogPartRevision,
	refreshData,
}: CopyRulesFromAnotherRevisionButtonProps) => {
	const { openDialog } = useGenericDialog();
	return (
		<AsyncButton
			label={i18n.t("import_rules_from_another_revision")}
			icon={faFileDownload}
			variant={"outlined"}
			onClick={async () => {
				const catalogParts = await CatalogPartQueryApi.getPartCategoryCatalogParts({
					partCategoryId: catalogPartRevision.categoryId,
				});
				openDialog(({ closeDialog }) => ({
					title: i18n.t("import_rules_from_another_revision"),
					agentContext: {
						key: defineAgentUiContextKey("erp.configurator.importCatalogPartRevisionAttributeRules.dialog"),
						title: i18n.t("import_rules_from_another_revision"),
						data: { targetCatalogPartRevisionId: catalogPartRevision.catalogPartRevisionId },
					},
					content: openDialogContent(catalogParts, catalogPartRevision, closeDialog, refreshData),
				}));
			}}
		/>
	);
};

function openDialogContent(
	catalogParts: Array<CatalogPart>,
	catalogPartRevision: CatalogPartRevisionView,
	closeDialog: (params?: CloseDialogParams) => Promise<void>,
	refreshData: (params?: Partial<object>) => Promise<object[]>,
) {
	return (
		<CatalogPartRevisionSelectionDataGrid
			catalogPartIds={catalogParts.map((cp) => cp.catalogPartId)}
			actionBarComponents={(selectedRows) => (
				<>
					<AsyncButton
						icon={faFileDownload}
						label={i18n.t("import")}
						variant={"outlined"}
						disabled={selectedRows.length === 0}
						onClick={async () => {
							const onlySelectedRow = selectedRows[0];
							if (onlySelectedRow == undefined) return;
							await CatalogPartRevisionAttributeRulesApi.copyRevisionAttributeRulesFromAnotherRevision({
								targetCatalogPartRevisionId: catalogPartRevision.catalogPartRevisionId,
								sourceCatalogPartRevisionId: onlySelectedRow.catalogPartRevisionId,
							});
							await closeDialog();
							await refreshData();
						}}
					/>
				</>
			)}
		/>
	);
}
